/**
 * StoreSelector — Radix Popover for switching between storefronts.
 *
 * Rendered in the sidebar when the Storefront module is expanded.
 * Shows current store with color dot + name. When tenant has 2+ stores,
 * shows a dropdown with "All Stores" + individual stores + "+ New Store".
 * Single-store merchants see just the store name (no dropdown).
 */

import { useState } from "react"
import { Check, ChevronDown, Globe, Plus } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useStoreList, getStoreColor, type StorefrontEntry } from "@/hooks/useStoreList"
import { useAuthStore } from "@/lib/store/authStore"
import { cn } from "@/lib/utils"
import { CreateStoreDialog } from "./CreateStoreDialog"

export function StoreSelector() {
  const [open, setOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const { data: stores = [] } = useStoreList()
  const activeKey = useAuthStore((s) => s.activeStorefrontKey)
  const setActive = useAuthStore((s) => s.setActiveStorefront)

  if (stores.length === 0) return null

  const activeStore = activeKey ? stores.find((s) => s.storefront_key === activeKey) : null
  const label = activeStore?.label ?? "All Stores"
  const colorClass = activeStore ? getStoreColor(activeStore.color_index) : null

  // Single store — static display, no dropdown
  if (stores.length === 1) {
    const store = stores[0]
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm">
        <span className={cn("w-2 h-2 rounded-full shrink-0", getStoreColor(store.color_index))} />
        <span className="truncate font-medium text-foreground/80">{store.label}</span>
      </div>
    )
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="flex items-center gap-2 px-3 py-2 w-full text-sm rounded-lg hover:bg-muted/50 transition-colors"
            aria-label="Select store"
          >
            {colorClass ? (
              <span className={cn("w-2 h-2 rounded-full shrink-0", colorClass)} />
            ) : (
              <Globe className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            )}
            <span className="truncate font-medium text-foreground/80">{label}</span>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground ml-auto shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={4} className="w-56 p-1">
          {/* All Stores option */}
          <button
            className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm rounded-md hover:bg-muted/50 transition-colors"
            onClick={() => { setActive(null); setOpen(false) }}
          >
            <Globe className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="truncate">All Stores</span>
            {!activeKey && <Check className="w-3.5 h-3.5 text-primary ml-auto shrink-0" />}
          </button>

          {/* Store list */}
          {stores.map((store) => (
            <StoreRow
              key={store.storefront_key}
              store={store}
              isActive={activeKey === store.storefront_key}
              onSelect={() => { setActive(store.storefront_key); setOpen(false) }}
            />
          ))}

          {/* Divider */}
          <div className="h-px bg-border/40 my-1" />

          {/* New Store */}
          <button
            className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm rounded-md hover:bg-muted/50 transition-colors text-muted-foreground"
            onClick={() => { setOpen(false); setDialogOpen(true) }}
          >
            <Plus className="w-3.5 h-3.5 shrink-0" />
            <span>New Store</span>
          </button>
        </PopoverContent>
      </Popover>

      <CreateStoreDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  )
}

function StoreRow({
  store,
  isActive,
  onSelect,
}: {
  store: StorefrontEntry
  isActive: boolean
  onSelect: () => void
}) {
  return (
    <button
      className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm rounded-md hover:bg-muted/50 transition-colors"
      onClick={onSelect}
    >
      <span className={cn("w-2 h-2 rounded-full shrink-0", getStoreColor(store.color_index))} />
      <span className="truncate">{store.label}</span>
      {store.is_primary ? (
        <span className="text-[10px] text-muted-foreground ml-1 shrink-0">Primary</span>
      ) : null}
      {isActive && <Check className="w-3.5 h-3.5 text-primary ml-auto shrink-0" />}
    </button>
  )
}
